import { Injectable } from '@angular/core';
import { MatSnackBar, MatSnackBarVerticalPosition } from '@angular/material/snack-bar';

@Injectable({
  providedIn: 'root'
})
export class SnackbarService {

  constructor(
    private snackBar: MatSnackBar,
  ) { }

  /**
   * Displays a success snackbar notification with customizable options.
   * 
   * @param message - The message to display in the snackbar. Defaults to "Succeeded" if not provided.
   * @param action - The action button text to display. Defaults to empty string if not provided.
   * @param configOptions - Optional configuration object for customizing the snackbar appearance and behavior.
   * @param configOptions.duration - Duration in milliseconds before the snackbar auto-dismisses. Defaults to 5000ms (5 seconds).
   * @param configOptions.position - Vertical position of the snackbar ('top' or 'bottom'). Defaults to 'bottom'.
   * @param configOptions.classes - Array of CSS class names to apply for custom styling. Defaults to ['success-snackbar'].
   * 
   * @example
   * ```typescript
   * // Basic usage with default message
   * this.snackbarService.showSuccessSnackbar();
   * 
   * // With custom message
   * this.snackbarService.showSuccessSnackbar('Profile updated successfully!');
   * 
   * // With custom message and action button
   * this.snackbarService.showSuccessSnackbar('Data saved!', 'View');
   * 
   * // With full configuration
   * this.snackbarService.showSuccessSnackbar(
   *   'Operation completed successfully!',
   *   'Close',
   *   { 
   *     duration: 3000, 
   *     position: 'top', 
   *     classes: ['custom-success-style'] 
   *   }
   * );
   * ```
   */
  public showSuccessSnackbar(message?: string, action?: string, configOptions?: { duration?: number, position?: MatSnackBarVerticalPosition, classes?: string[]}): void {
    this.snackBar.open(
      message ?? `Succeeded`,
      action ?? '',
      {        duration: configOptions?.duration ?? 5000,
        verticalPosition: configOptions?.position ?? 'bottom',
        panelClass: (configOptions?.classes && configOptions?.classes.length > 0) ? configOptions?.classes : ['success-snackbar'],
      },
    )
  }
  
  /**
   * Displays an error snackbar notification with customizable options.
   * 
   * @param message - The error message to display in the snackbar. Defaults to "Failed" if not provided.
   * @param action - The action button text to display. Defaults to "Close" if not provided.
   * @param configOptions - Optional configuration object for customizing the snackbar appearance and behavior.
   * @param configOptions.duration - Duration in milliseconds before the snackbar auto-dismisses. Defaults to no auto-dismiss.
   * @param configOptions.position - Vertical position of the snackbar ('top' or 'bottom'). Defaults to 'bottom'.
   * @param configOptions.classes - Array of CSS class names to apply for custom styling. Defaults to ['error-snackbar'].
   * 
   * @example
   * ```typescript
   * // Basic usage with default message
   * this.snackbarService.showErrorSnackbar();
   * 
   * // With custom error message
   * this.snackbarService.showErrorSnackbar('Failed to save data. Please try again.');
   * 
   * // With custom message and retry action
   * this.snackbarService.showErrorSnackbar('Network error occurred', 'Retry');
   * 
   * // With full configuration
   * this.snackbarService.showErrorSnackbar(
   *   'Validation failed: Please check your input',
   *   'Dismiss',
   *   { 
   *     duration: 7000, 
   *     position: 'top', 
   *     classes: ['custom-error-style', 'bold-text'] 
   *   }
   * );
   * ```
   */
  public showErrorSnackbar(message?: string, action?: string, configOptions?: { duration?: number, position?: MatSnackBarVerticalPosition, classes?: string[] }): void {
    this.snackBar.open(
        message ?? `Failed`,
        action ?? 'Close',
        {
            duration: configOptions?.duration ?? 10000, // Default to auto-dismiss after 10 seconds,
            verticalPosition: configOptions?.position ?? 'bottom',
            panelClass: (configOptions?.classes && configOptions?.classes.length > 0) ? configOptions?.classes : ['error-snackbar'],
        },
    );
  }
}
